import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import { EntityList, PageHead } from '../../components/EntityList';
import AssetService from '../../services/asset.service';
import { getError } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import BillsEditor from './BillsEditor';

function BillEditorSkeleton() {
	return (
		<div className='tw:rounded-panel tw:border tw:border-border tw:bg-surface tw:p-6' aria-busy='true' aria-live='polite'>
			<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
				{Array.from({ length: 8 }, (_, index) => (
					<div key={index} className='tw:space-y-2'>
						<span className='el-sk tw:block tw:h-[11px] tw:w-24' />
						<span className='el-sk tw:block tw:h-[34px] tw:w-full' />
					</div>
				))}
			</div>
			<div className='tw:mt-6 tw:flex tw:justify-end tw:gap-2'>
				<span className='el-sk tw:block tw:h-[34px] tw:w-24' />
				<span className='el-sk tw:block tw:h-[34px] tw:w-24' />
			</div>
		</div>
	);
}

function NewBillsNew() {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const { state } = useLocation();
	const [asset, setAsset] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let isCurrent = true;
		AssetService.getDetails(id)
			.then(({ data }) => {
				if (isCurrent) setAsset(data);
			})
			.catch((ex) => toast.error(getError(ex)))
			.finally(() => {
				if (isCurrent) setLoading(false);
			});
		return () => {
			isCurrent = false;
		};
	}, [id]);

	// Come back to the Gastos tab the user launched from, and seed the type from
	// it — creating from "Gastos eventuales" must not default to Periodico.
	const returnTo = state?.returnTo || `/AdminScreen/assets/asset/${id}/bills-eventual`;
	const seededType = state?.billType === 'Periodico' ? 'Periodico' : 'Eventual';

	return (
		<EntityList>
			<Helmet>
				<title>{`Agregar ${t('entity.bill')}`}</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.asset.plural'), to: '/AdminScreen/assets' },
					{ label: asset?.customId || t('entity.asset'), to: `/AdminScreen/assets/asset/${id}` },
					{ label: `Agregar ${t('entity.bill')}` },
				]}
				title={`Agregar ${t('entity.bill')}`}
				subtitle='Completá los datos para crear el gasto.'
			/>
			{loading ? (
				<BillEditorSkeleton />
			) : (
				<BillsEditor
					mode='create'
					initialData={{ asset, type: seededType }}
					typeLocked
					onCancel={() => navigate(returnTo)}
					onSaved={() => navigate(returnTo)}
					saveLabel='Agregar'
				/>
			)}
		</EntityList>
	);
}

export default NewBillsNew;
